import { useCallback, useEffect, useMemo, useState } from 'react';

import ShowLessIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { TreeItem, TreeView } from '@mui/lab';
import {
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  SelectProps,
  Typography,
} from '@mui/material';
import axios from 'axios';
import { FieldValues, useController, UseControllerProps } from 'react-hook-form';

import strings, { url } from 'constants/index';
import { ColumnIds } from 'enums';
import { buildRequestParams } from 'utils/pagination.utils';

interface RoundsTreeFormFieldProps extends Omit<SelectProps, 'name' | 'defaultValue'> {
  label?: string;
  required?: boolean;
  rounds: Entity[];
}

export function RoundsTreeFormField<T extends FieldValues>({
  name,
  label,
  control,
  defaultValue,
  required,
  rules,
  rounds,
  disabled,
  ...selectProps
}: UseControllerProps<T> & RoundsTreeFormFieldProps) {
  const [selected, setSelected] = useState<number>();
  const [roundsCache, setRoundsCache] = useState<{ [_ in string]: Array<Entity> }>({});

  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
    defaultValue,
    rules: {
      required: { value: required, message: strings.field_is_required },
      ...rules,
    },
  });

  const getRounds = useCallback(async selected => {
    const { data } = await axios.get(url.rounds, {
      params: buildRequestParams({
        pageSize: 200,
        filters: [{ columnId: ColumnIds.ParentRoundId, value: selected }],
      }),
    });
    setRoundsCache(prev => ({
      ...prev,
      [selected]: data.items?.map(({ id, name }) => ({ id, name })),
    }));
  }, []);

  const renderTreeItems = useCallback(
    (rounds: Array<Entity>) => {
      const items = rounds.map(({ id, name }) => (
        <TreeItem
          key={id}
          nodeId={`${id}`}
          label={
            <Typography id={name} sx={{ my: '4px' }}>
              {name}
            </Typography>
          }
        >
          {!!roundsCache[id]?.length && renderTreeItems(roundsCache[id])}
        </TreeItem>
      ));

      return items;
    },
    [roundsCache],
  );

  const items = useMemo(() => renderTreeItems(rounds), [renderTreeItems, rounds]);

  useEffect(() => {
    if (selected && !roundsCache[selected]) {
      getRounds(selected);
    }
  }, [selected, getRounds, roundsCache]);

  return (
    <FormControl
      disabled={disabled}
      variant="standard"
      fullWidth
      error={!!error}
      required={required}
    >
      <InputLabel>{strings.round}</InputLabel>
      <Select
        {...field}
        data-testid={name}
        label={strings.round}
        variant="standard"
        fullWidth
        value={field.value?.id || ''}
        onChange={() => false}
        disabled={disabled}
        {...selectProps}
      >
        <TreeView
          defaultCollapseIcon={<ExpandMoreIcon />}
          defaultExpandIcon={<ShowLessIcon />}
          onNodeSelect={(e, nodeIds: string) => {
            const selectedId = Number(nodeIds);
            const roundEntity =
              rounds.find(r => r.id === selectedId) ||
              Object.values(roundsCache)
                .flat()
                .find(r => r.id === selectedId);
            const value = {
              id: selectedId,
              name: roundEntity?.name || (e.target as HTMLElement)?.textContent || '',
            };
            field.onChange(value);
            setSelected(value.id);
          }}
        >
          {items}
        </TreeView>
        <MenuItem
          data-testid="hidden-option"
          sx={{ display: 'none' }}
          value={field.value?.id || ''}
        >
          {field.value?.name || ''}
        </MenuItem>
      </Select>
      {error && <FormHelperText title="select-error">{error.message}</FormHelperText>}
    </FormControl>
  );
}
